import { Form } from '@mairistem/composants';
import type { ErrorField } from '@mairistem/fiche/lib/ts/types';
import { Combobox, type TemplateRequestGet } from '@mairistem/finances-utils';
import { isNewIdentifiant, request } from '@mairistem/tools';
import React, { useState } from 'react';
import ProjetComposantInput from '@/src/components/SubventionFichePanel/Generalite/SubventionProjet/ProjetComposantInput';
import type { Composant } from '@/src/interfaces/composant';
import type { Projet } from '@/src/interfaces/projet';
import { getAssietteEligible } from '@/src/utils/subvention';
import { isError } from '@/src/utils/tools';

interface SubventionProjetProps {
	projet: Projet;
	selectedComposants: Partial<Composant>[];
	identifiantEntite: number;
	errors: Array<ErrorField>;
	onChange: any;
	identifiant: number;
	plafondEligible: number;
	realisationHT: number;
	tauxAide: number;
}

const SubventionProjet = ({
	projet: projetProps = null,
	selectedComposants = null,
	identifiantEntite = null,
	errors = null,
	onChange = null,
	identifiant = null,
	plafondEligible = 0,
	realisationHT = 0,
	tauxAide = 0,
}: SubventionProjetProps) => {
	const [projets, setProjets] = useState([]);
	const [projet, setProjet] = useState(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: On a besoin de 'identifiant'
	React.useEffect(() => {
		setProjet(projetProps);
	}, [projetProps, identifiant]);

	React.useEffect(() => {
		if (identifiantEntite && identifiant) {
			request
				.get<unknown, TemplateRequestGet<Projet>>(
					`/api/finances/subventionsPercues/projets/?identifiantEntite=${identifiantEntite}&all=true&sort=libelle:asc&filterOnStatus=true${!isNewIdentifiant(identifiant)}`,
				)
				// Récupération des projets non cloturés et du projet sur la subvention même si il est cloturé
				.then(({ data }) => {
					setProjets(
						data.map((newProjet) => ({
							key: newProjet.identifiant,
							value: newProjet.identifiant,
							text: newProjet.libelle,
						})),
					);
				});
		}
	}, [identifiantEntite, identifiant]);

	const handleChange = (_e, { value }) => {
		request
			.get<unknown, Projet>(
				`/api/finances/subventionsPercues/projets/${value}?withComposants=true`,
			)
			.then((data) => {
				setProjet(data);

				if (onChange) {
					const assietteEligible = getAssietteEligible(
						plafondEligible,
						data?.composants,
						realisationHT,
					);

					onChange({
						identifiant,
						identifiantProjet: data?.identifiant,
						projet: data,
						composants: data?.composants,
						assietteEligible,
						montantEnvisage: (assietteEligible * tauxAide) / 100,
					});
				}
			});
	};

	return (
		<>
			<Form.Field
				control={Combobox}
				data-testid="projetSubvention"
				error={isError(errors, 'subventionspercues-projet')}
				// @ts-expect-error
				fluid
				id="subventionspercues-projet"
				key="projet"
				label="Projet associé"
				name="projet"
				onChange={handleChange}
				options={projets}
				placeholder="Projet associé"
				required
				search
				selection
				value={projet?.identifiant ?? null}
			/>

			{projet?.composants?.length > 1 && (
				<ProjetComposantInput
					composants={projet?.composants}
					selectedComposants={selectedComposants}
					onChange={onChange}
					identifiant={identifiant}
					plafondEligible={plafondEligible}
					realisationHT={realisationHT}
					tauxAide={tauxAide}
				/>
			)}
		</>
	);
};

export default SubventionProjet;
